import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import { MuiTelInput, type MuiTelInputCountry } from "mui-tel-input";
import { Controller, useFormContext } from "react-hook-form";
import Block from "@/components/Block";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { getGuestPersons, getTestData, getTotalGuests } from "@/utils/booking";
import type { BookingFormData } from "../Details";

export default function PersonalInfoForm() {
  const { data: campgroundData } = useCampground();
  const { state: bookingState } = useBookingContext();
  const {
    control,
    watch,
    formState: { errors },
    reset,
  } = useFormContext<BookingFormData>();

  const totalGuests = getTotalGuests(
    bookingState.selectedPersons,
    campgroundData.campground.persons,
    "human",
  );

  const countries = Object.entries(campgroundData.countries).map(([code, label]) => ({
    label,
    value: code,
  }));

  const selectedCountryCode = (watch("country")?.code?.toUpperCase() || "DE") as MuiTelInputCountry;

  return (
    <Block flushOnMobile>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h3" sx={{ mb: 2 }}>
            <Trans>Personal Information</Trans>
          </Typography>
          <Stack spacing={1}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => {
                  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    field.onChange(e);

                    if (value === "cnctest") {
                      const personIds = getGuestPersons(
                        bookingState.selectedPersons,
                        campgroundData.campground.persons,
                      ).map((p) => p.id);
                      reset(getTestData(totalGuests, personIds));
                    }
                  };

                  return (
                    <TextField
                      {...field}
                      onChange={handleNameChange}
                      label={t`First Name`}
                      sx={{ flex: 1 }}
                      required
                      variant="filled"
                      error={!!errors.name}
                      helperText={errors.name?.message as string | undefined}
                    />
                  );
                }}
              />
              <Controller
                name="lastname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t`Last Name`}
                    sx={{ flex: 1 }}
                    required
                    variant="filled"
                    error={!!errors.lastname}
                    helperText={errors.lastname?.message as string | undefined}
                  />
                )}
              />
            </Stack>
          </Stack>
        </Box>
        <Box>
          <Typography variant="h3" sx={{ mb: 2 }}>
            <Trans>Address</Trans>
          </Typography>
          <Stack spacing={1}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    disablePortal
                    options={countries}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => {
                      if (!value || typeof value !== "object" || !("code" in value)) {
                        return false;
                      }
                      return option.value === value.code;
                    }}
                    onChange={(_, newValue) => {
                      field.onChange(newValue ? { code: newValue.value } : { code: "" });
                    }}
                    value={countries.find((c) => c.value === field.value.code) || null}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Country`}
                        variant="filled"
                        required
                        error={!!errors.country?.code}
                        helperText={errors.country?.code?.message}
                      />
                    )}
                  />
                )}
              />
              <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Controller
                name="street"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t`Address Line 1`}
                    sx={{ flex: 1 }}
                    required
                    variant="filled"
                    error={!!errors.street}
                    helperText={errors.street?.message as string | undefined}
                  />
                )}
              />
              <Controller
                name="street_additional"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t`Address Line 2`}
                    sx={{ flex: 1 }}
                    variant="filled"
                    error={!!errors.street_additional}
                    helperText={errors.street_additional?.message as string | undefined}
                  />
                )}
              />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Controller
                name="zip"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t`Postal Code`}
                    required
                    variant="filled"
                    sx={{ flex: 1 }}
                    error={!!errors.zip}
                    helperText={errors.zip?.message as string | undefined}
                  />
                )}
              />
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t`City`}
                    sx={{ flex: 1 }}
                    required
                    variant="filled"
                    error={!!errors.city}
                    helperText={errors.city?.message as string | undefined}
                  />
                )}
              />
            </Stack>
          </Stack>
        </Box>
        <Box>
          <Typography variant="h3" sx={{ mb: 2 }}>
            <Trans>Contact Information</Trans>
          </Typography>
          <Stack spacing={1}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Controller
                name="emails.mail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t`Email`}
                    type="email"
                    sx={{ flex: 1 }}
                    required
                    variant="filled"
                    error={!!errors.emails?.mail}
                    helperText={errors.emails?.mail?.message}
                  />
                )}
              />
              <Controller
                name="emails.confirmMail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t`Confirm Email`}
                    type="email"
                    sx={{ flex: 1 }}
                    required
                    variant="filled"
                    error={!!errors.emails?.confirmMail}
                    helperText={errors.emails?.confirmMail?.message}
                  />
                )}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller
                name="phone"
                control={control}
                key={selectedCountryCode} // Force remount when country changes
                render={({ field }) => (
                  <MuiTelInput
                    {...field}
                    sx={{
                      flex: 1,
                      ".MuiInputAdornment-root": {
                        margin: "0 !important",
                      },
                    }}
                    variant="filled"
                    forceCallingCode={true}
                    defaultCountry={selectedCountryCode}
                    label={t`Phone`}
                    placeholder={t`Phone`}
                    error={!!errors.phone}
                    helperText={errors.phone?.message as string | undefined}
                  />
                )}
              />
              <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Block>
  );
}
